/**
 * insertAfterPlan.js — story 167 (D5 · RN-08.4, RN-08.5, RN-08.7). Pure: no React, no server.
 *
 * What changes when one or more new nodes are dropped behind X on a row of the mini tree:
 *  - the FIRST OK result of X (model order) keeps its id and its configuration and points to the first
 *    new node; any other OK result of X goes away (S92: X ends with a single OK result);
 *  - each new node points to the next one with a default OK result;
 *  - the last new node gets a default OK result to every distinct former OK target of X (null = end);
 *  - X's FAIL results are untouched and the new nodes are born without FAIL (RN-08.4, D13/DL-05);
 *  - `isFirst` is not touched.
 * The ids come from `createId`, so the reducer applies a finished plan and stays deterministic.
 */
export const INSERT_GAP_PX = 80
/** TechnologyNode.jsx:378 / SubWorkflowNode.jsx:94: a node is never narrower than 6rem. */
export const UNMEASURED_NODE_WIDTH_PX = 96

const OK = 'OK'

const defaultOkResult = (id, nodeId, nextNode) => ({
  id,
  nodeId,
  operationResult: OK,
  nextNode,
  connectivity: 'FLEXIBLE',
  eventType: null,
  eventValue: null,
  startDate: null,
  timezone: null
})

/**
 * @param {Object} afterNode X, a node of the level the rows of the mini tree edit (the first level)
 * @param {string[]} insertedNodeIds The new nodes, in chain order (the order of the process tree, RN-08.7)
 * @param {function(): string} createId
 */
export const planInsertAfter = (afterNode, insertedNodeIds, createId) => {
  const results = afterNode.nodeResults || []
  const okResults = results.filter((result) => result.operationResult === OK)
  const firstId = insertedNodeIds[0]
  const lastId = insertedNodeIds[insertedNodeIds.length - 1]
  const linkResult = okResults.length > 0
    ? { ...okResults[0], nextNode: firstId }
    : defaultOkResult(createId(), afterNode.id, firstId)
  const afterNodeResults = okResults.length > 0
    ? results.filter((result) => result.operationResult !== OK || result === okResults[0]).map((result) => (result === okResults[0] ? linkResult : result))
    : [...results, linkResult]
  const formerTargets = [...new Set(okResults.map((result) => result.nextNode || null))]
  const resultsByNodeId = {}
  insertedNodeIds.slice(0, -1).forEach((nodeId, index) => {
    resultsByNodeId[nodeId] = [defaultOkResult(createId(), nodeId, insertedNodeIds[index + 1])]
  })
  resultsByNodeId[lastId] = formerTargets.map((target) => defaultOkResult(createId(), lastId, target))
  return {
    afterNodeId: afterNode.id,
    afterNodeResults,
    linkResultId: linkResult.id,
    replacedResultIds: okResults.map((result) => result.id),
    resultsByNodeId
  }
}

/** RN-08.5, D16: at the right of X, at X's `y`, its left edge 80 px after X's painted right edge. */
export const positionRightOf = (canvasNode, index = 0) => {
  const width = canvasNode.width || UNMEASURED_NODE_WIDTH_PX
  return {
    x: canvasNode.position.x + width + INSERT_GAP_PX + index * (UNMEASURED_NODE_WIDTH_PX + INSERT_GAP_PX),
    y: canvasNode.position.y
  }
}

/**
 * The canvas edge of a node result, with the SAME shape `utils.js` builds when the whole graph is projected
 * (`getWorkflowNodesAndEdges`, :143-171, and `endEdge`, :54-76), so an inserted edge behaves like a loaded one.
 */
export const edgeOfNodeResult = (sourceNodeId, result, { onDeleteEdge, onUpdateEdge, workflowInUse = false }) => {
  const handle = result.operationResult === OK ? OK : 'FAIL'
  const toEnd = !result.nextNode
  return {
    id: result.id,
    source: sourceNodeId,
    sourceHandle: handle,
    target: toEnd ? 'end' : result.nextNode,
    targetHandle: 'target',
    type: 'custom',
    data: {
      selected: false,
      type: handle,
      connectivity: toEnd ? 'FLEXIBLE' : result.connectivity,
      eventType: result.eventType,
      eventValue: result.eventValue,
      edgeCardVisible: true,
      startDate: result.startDate,
      timezone: result.timezone,
      disableEdges: false,
      workflowInUse,
      updateEdge: onUpdateEdge,
      deleteEdge: onDeleteEdge
    }
  }
}

/** The canvas edges once the plan is applied: X's former OK edges go, the plan's edges come in. */
export const applyInsertionToEdges = (edges, plan, toEdge) => {
  const replaced = new Set(plan.replacedResultIds)
  const linkResult = plan.afterNodeResults.find((result) => result.id === plan.linkResultId)
  const newEdges = [
    toEdge(plan.afterNodeId, linkResult),
    ...Object.entries(plan.resultsByNodeId).flatMap(([nodeId, results]) => results.map((result) => toEdge(nodeId, result)))
  ]
  return edges.filter((edge) => !replaced.has(edge.id)).concat(newEdges)
}
